import type { JSX, MouseEventHandler } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import cc from 'classcat'

import { setMboMenuMode } from '../store/actions'

export type MboMenuItem = {
  menuId: string
  menualias: string
  title: string
  iconClass: string
  uri: string
  status: string
  children: MboMenuItem[]
}

type MenuItemProps = {
  menualias?: string
  isOpen?: boolean
  iconClass?: string
  title?: string
  uri?: string
  mboBaseUrl: string
  buildMenuItem: (item: MboMenuItem) => JSX.Element | null
  switchToEditor?: MouseEventHandler
  closeMenu?: MouseEventHandler
  openMenu?: (entry?: string) => MouseEventHandler
  children?: MboMenuItem[]
}

export function MenuItem({
  switchToEditor,
  closeMenu,
  openMenu,
  mboBaseUrl,
  buildMenuItem,
  children,
  menualias,
  isOpen,
  iconClass,
  uri,
  title,
}: Readonly<MenuItemProps>) {
  const isEditorLink = menualias?.toLowerCase() === 'menu-editor'
  const hasChildren = children ? children.length > 0 : false
  const className = cc([
    menualias?.toLowerCase() || '',
    {
      active: isEditorLink,
      'ui-hover': isOpen,
      hasChildren,
    },
  ])
  const iconClassName = cc([
    'mainmenu-icon',
    'fa',
    'RightPadding',
    iconClass && iconClass.replace('icon-', 'ep-icomoon-'),
  ])
  function getOnClickHandler(): MouseEventHandler | undefined {
    return isEditorLink ? switchToEditor : isOpen ? closeMenu : openMenu?.(menualias)
  }

  return (
    <li className={className}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href={hasChildren || isEditorLink ? '#' : mboBaseUrl + uri}
        className="item-title"
        target="_parent"
        onClick={!hasChildren && !isEditorLink ? undefined : getOnClickHandler()}
      >
        <span className={iconClassName} />
        {title}
        {!hasChildren ? null : (
          <span
            className={cc([
              'fa FloatRight submenu-marker',
              {
                'fa-angle-up': isOpen,
                'fa-angle-down': !isOpen,
              },
            ])}
          />
        )}
      </a>
      {hasChildren ? <ul>{children?.map(buildMenuItem)}</ul> : null}
    </li>
  )
}

export default function MBOMenu() {
  const dispatch = useDispatch<GlobalDispatch>()
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))
  const menu = useSelector<State, MboMenuItem[]>((state) => state.getIn(['view', 'mboMenu']))

  const [currentMenu, setCurrentMenu] = useState<MboMenuItem[]>([])
  const [openMenuEntry, setOpenMenuEntry] = useState<string | undefined>()

  // The parent window (MBO) sends the MBO menu with a window "message" event
  // that we subscribe to in the entry "app.js" file. It is dispatched to the
  // store in the "message" event handler.
  //
  // Even though "MBO_MENU" will be sent only once upon initialization of the
  // page, it is a side effect that can cause a React hydration error, hence
  // the local `currentMenu` state.
  useEffect(() => {
    setCurrentMenu(menu)
  }, [menu])

  const hideMboMenu = () => dispatch(setMboMenuMode(false))

  const itemProps: MenuItemProps = {
    mboBaseUrl,
    buildMenuItem,
    switchToEditor: (event) => {
      event.preventDefault()
      hideMboMenu()
    },
    openMenu: (alias) => (event) => {
      event.preventDefault()
      setOpenMenuEntry(alias)
    },
    closeMenu: (event) => {
      event.preventDefault()
      setOpenMenuEntry(undefined)
    },
  }

  function buildMenuItem(item: MboMenuItem) {
    return item === null || item.status === 'HIDDEN' ? null : (
      <MenuItem
        key={item.menuId || item.menualias}
        {...item}
        {...itemProps}
        isOpen={openMenuEntry === item.menualias}
      />
    )
  }

  return (
    <ul id="mainMenu" className="MainMenuBar">
      {currentMenu.map((item) => buildMenuItem({ ...item, ...itemProps }))}
    </ul>
  )
}
